import React, { memo } from 'react';
import { StyleProp, StyleSheet, TouchableOpacity, ViewStyle } from 'react-native';

import { EventInfo, isIndividualEvent, isTeamEvent } from '@/api/events/types/types';
import ChevronRight from '@/assets/icons/chevronRight';
import { GameTrackerButton } from '@/components/GameTrackerButton';
import MergedLogos from '@/components/MergedLogos';
import { OneTimeTooltipGameTracker } from '@/components/OneTimeTooltipGameTracker';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { TeamLogos } from '@/components/scoreboard/types';
import { LeagueLogo } from '@/feature/entries-pickem/components/LeagueLogo';
import { designSystem } from '@/styles/styles';
import { EventStatus, Status } from '@/types/api.generated';
import { NFL_FULL_SEASON } from '@/utils/constants';
import { FloatingModalConfig } from '@/utils/floatingModal/FloatingModalProvider';
import { gameUtils } from '@/utils/games';
import { isCollegeSport, nflFullSeason } from '@/utils/league';

export type EntrySectionEventHeaderProps = {
    event?: EventInfo;
    status?: Status;
    gameTrackerConfig?: FloatingModalConfig;
    showGameTrackerTooltip?: boolean;
    onViewFullEvent?: () => void;
};

/**
 * This header is used for events of sports that don't support the scoreboard or for when the event hasn't started yet.
 * @param event
 * @returns
 */
export const BasicEventHeader = memo(
    ({ event, gameTrackerConfig, showGameTrackerTooltip, onViewFullEvent }: EntrySectionEventHeaderProps) => {
        const titleLabel = event ? `${gameUtils.createTitleLabel(event)} ` : '';
        const dateLabel = event?.date ? gameUtils.createTimeLabel(event, undefined, false) : '';
        const isFullSeason = nflFullSeason(event?.league);
        const fullTitle = isFullSeason ? NFL_FULL_SEASON : titleLabel;
        const showLeagueLogo = isIndividualEvent(event) || isFullSeason;
        const eventStatus = event?.status;
        const isLive = eventStatus === EventStatus.InProgress;
        const showGameTracker = isLive && gameTrackerConfig;

        const shouldHighlightLiveEvent = event ? gameUtils.isLive(event) : false;

        let teamsLogo: TeamLogos | undefined;
        let teamShortCodes;

        if (isTeamEvent(event) && !showLeagueLogo) {
            const [away, home] = event.teams;
            teamsLogo = [
                { icon: away.icon, color: away.color },
                { icon: home.icon, color: home.color },
            ];
            teamShortCodes = [away.name, home.name];
        }

        const logoSizes: [number, number] = isCollegeSport(event?.league) ? [24, 24] : [16, 16];

        const headerBackgroundColor = ds.colorValue.background.neutral.subtler;

        return (
            <BorderTopWrapper style={[styles.topBorder, { backgroundColor: headerBackgroundColor }]}>
                <Row style={styles.headerWrapper}>
                    <Row alignItems={'center'} flex={1}>
                        {teamsLogo ? (
                            <Box>
                                <MergedLogos
                                    logos={teamsLogo}
                                    borderColor={headerBackgroundColor}
                                    renderMode="team"
                                    overlap={6}
                                    logoSizes={logoSizes}
                                    backdropSizes={[28, 24]}
                                    imageShape="square"
                                    borderRadius={8}
                                    teamShortCodes={teamShortCodes}
                                />
                            </Box>
                        ) : null}
                        {showLeagueLogo && event?.league ? <LeagueLogo league={event.league} /> : null}
                        <Row flex={1}>
                            <Text
                                marginLeft={'s8'}
                                variant="bodySmall"
                                color={'neutral300'}
                                numberOfLines={1}
                                ellipsizeMode="tail"
                                style={styles.title}
                            >
                                {fullTitle}
                            </Text>
                            {!isFullSeason ? (
                                <Text
                                    variant="bodySmall"
                                    color="neutral300"
                                    style={shouldHighlightLiveEvent ? styles.liveStyle : {}}
                                >
                                    • {dateLabel}
                                </Text>
                            ) : null}
                        </Row>
                    </Row>
                    {showGameTracker ? (
                        <Box marginLeft={'s12'}>
                            {showGameTrackerTooltip ? (
                                <OneTimeTooltipGameTracker
                                    gameTrackerConfig={gameTrackerConfig}
                                    screenName={'Picks_EntryScreen_GameTrackerTooltip'}
                                    tooltipContentPosition={{ right: -14, top: 32 }}
                                />
                            ) : (
                                <GameTrackerButton config={gameTrackerConfig} />
                            )}
                        </Box>
                    ) : null}
                    {onViewFullEvent ? (
                        <TouchableOpacity onPress={onViewFullEvent} style={styles.viewFullEvent}>
                            <Row paddingVertical="s16" alignItems="center">
                                <Text variant="bodySmall" color="neutral300">
                                    View event
                                </Text>
                                <ChevronRight color={designSystem.colors.neutral300} />
                            </Row>
                        </TouchableOpacity>
                    ) : null}
                </Row>
            </BorderTopWrapper>
        );
    }
);

export const BorderTopWrapper = ({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) => {
    return (
        <Box
            borderWidth={1}
            borderTopLeftRadius={'r16'}
            borderTopRightRadius={'r16'}
            style={[styles.borderWrapperColor, style]}
        >
            {children}
        </Box>
    );
};

const styles = StyleSheet.create({
    liveStyle: {
        color: ds.colorValue.text.danger,
        fontWeight: '600',
        marginLeft: 4,
    },
    headerWrapper: {
        flex: 1,
        padding: 16,
        height: 56,
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    topBorder: {
        borderCurve: 'continuous',
    },
    title: {
        flexShrink: 1,
    },
    viewFullEvent: {
        marginLeft: 12,
    },
    borderWrapperColor: {
        borderColor: ds.colorValue.border.subtle,
        borderBottomColor: ds.colorValue.border.strong,
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
});
